import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { EntitlementsService } from 'app/core/entitlements/entitlements.service';

/** What the caller wants said, in the operator's terms rather than the plan's. */
export interface UpsellDialogData {
    /** The thing they cannot do: "International operations", "Add crew member". */
    title: string;

    /** Why, said plainly: "You are using 2 of 2 crew seats". */
    reason?: string | null;

    /** What the step up gives instead. */
    detail?: string | null;

    /** Three or four things it unlocks. More is a pricing page, not an answer. */
    bullets?: string[];
}

/**
 * The offer, before the pricing page.
 *
 * A padlock that drops someone straight onto a table of plans makes them work
 * out for themselves which rung they needed and why they were sent there. This
 * answers the question they actually asked -- what is this, and what does it
 * cost -- and only then offers the plans.
 *
 * "Not now" is a real answer and is given equal room. Somebody who came here
 * to add a pilot and found a wall should be able to get back to what they were
 * doing without hunting for a close button.
 */
@Component({
    selector: 'app-upsell-dialog',
    standalone: true,
    imports: [MatButtonModule, MatDialogModule, MatIconModule, TranslocoModule],
    template: `
        <div class="relative p-6 sm:p-8" *transloco="let t">
            <button
                mat-icon-button
                class="absolute right-3 top-3 rounded-full bg-primary text-white hover:bg-primary/90"
                [attr.aria-label]="t('close')"
                (click)="notNow()"
            >
                <mat-icon class="text-white" [svgIcon]="'heroicons_solid:x-mark'" />
            </button>

            <div
                class="flex h-12 w-12 items-center justify-center rounded-2xl border
                       border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
                <mat-icon class="icon-size-6 text-amber-700 dark:text-amber-400"
                          [svgIcon]="'heroicons_solid:lock-closed'" />
            </div>

            <h2 class="m-0 mt-5 text-2xl font-extrabold tracking-tight">{{ data.title }}</h2>

            @if (data.reason) {
                <p class="m-0 mt-3 text-secondary">{{ data.reason }}</p>
            }

            @if (offer()) {
                <p class="m-0 mt-1 text-secondary">{{ offer() }}</p>
            }

            @if (data.bullets?.length) {
                <ul class="m-0 mt-5 flex list-disc flex-col gap-1.5 pl-5">
                    @for (bullet of data.bullets; track bullet) {
                        <li>{{ bullet }}</li>
                    }
                </ul>
            }

            <div class="mt-7 flex flex-col gap-3 sm:flex-row">
                <button mat-flat-button color="primary" class="rounded-xl" (click)="seePlans()">
                    {{ cta() }}
                </button>
                <button mat-stroked-button class="rounded-xl" (click)="notNow()">
                    {{ t('not_now') }}
                </button>
            </div>
        </div>
    `,
})
export class UpsellDialogComponent {
    readonly data = inject<UpsellDialogData>(MAT_DIALOG_DATA);

    private readonly _dialogRef = inject(MatDialogRef<UpsellDialogComponent>);
    private readonly _router = inject(Router);
    private readonly _entitlements = inject(EntitlementsService);
    private readonly _transloco = inject(TranslocoService);

    /**
     * "Move to Essential · $39".
     *
     * Naming the plan and the price on the button itself is the difference
     * between a decision and a trip to a table to make one. When neither is
     * known -- at the top of the ladder, or before the plans have loaded --
     * it falls back to offering the plans.
     */
    cta(): string {
        const plan = this.nextPlan();

        if (!plan?.name) {
            return this._transloco.translate('see_plans');
        }

        return plan.priceCents
            ? this._transloco.translate('upsell_move_to_priced', {
                  plan: plan.name,
                  price: `$${Math.round(plan.priceCents / 100)}`,
              })
            : this._transloco.translate('upsell_move_to', { plan: plan.name });
    }

    /** What the caller said, or failing that what the next rung is called. */
    offer(): string | null {
        if (this.data.detail) {
            return this.data.detail;
        }

        const plan = this.nextPlan();

        return plan?.name
            ? this._transloco.translate('upsell_included_from', { plan: plan.name })
            : null;
    }

    /**
     * The button's whole job is to arrive at the plans.
     *
     * Closing first and navigating after left the two unrelated: anything
     * that refused the navigation — a guard, a resolver, a route that is not
     * where it was — simply dropped it, and the button looked broken. Now the
     * navigation is waited on, and a full page load is the fallback.
     */
    seePlans(): void {
        const plans = '/settings/plan-billing';

        this._router
            .navigateByUrl(plans)
            .then((ok) => {
                this._dialogRef.close('plans');

                if (!ok) {
                    window.location.assign(plans);
                }
            })
            .catch(() => {
                this._dialogRef.close('plans');
                window.location.assign(plans);
            });
    }

    notNow(): void {
        this._dialogRef.close(null);
    }

    /**
     * The API's answer first; the local catalogue carries the price.
     *
     * The server knows which plan is actually next for this workspace, but
     * reports only its name and limits -- the price lives in the catalogue the
     * tier screens were built from.
     */
    private nextPlan(): { name: string; priceCents: number | null } | null {
        const fromApi = this._entitlements.nextPlanFromApi();
        const local = this._entitlements.nextPlan();

        const name = fromApi?.name ?? local?.planName ?? null;

        if (!name) {
            return null;
        }

        return { name, priceCents: local?.priceCents ?? null };
    }
}
